import { useCallback, useEffect, useRef, useState } from 'react';
import type { Falha } from '../../acesso';
import { inteiro, lerPainelDoDia, reais } from '../../paineis';
import type { FrescorDaFonte, PainelDoDia } from '../../paineis';

/**
 * O que a Home (tela 05) e o Modo TV (tela 12 C) dividem: a leitura do painel do dia com o
 * último dado bom guardado (offline mostra a hora dele), a saudação pelo relógio de Brasília e
 * os textos de frescor. Nada aqui cria número — só formata o que o servidor mandou.
 */

export type EstadoDoPainel =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'pronto'; readonly dados: PainelDoDia; readonly lidoEm: number }
  | {
      readonly fase: 'erro';
      readonly falha: Falha;
      readonly ultimo: { readonly dados: PainelDoDia; readonly lidoEm: number } | null;
    };

/** Lê o painel; se `intervaloMs`, relê sozinho. Voltar para a aba também relê. */
export function usePainelDoDia(dia: string | undefined, intervaloMs?: number) {
  const [estado, setEstado] = useState<EstadoDoPainel>({ fase: 'carregando' });
  const pedido = useRef(0);

  const carregar = useCallback(async () => {
    const n = ++pedido.current;
    const r = await lerPainelDoDia(dia);
    if (n !== pedido.current) return;
    if (r.ok) setEstado({ fase: 'pronto', dados: r.dados, lidoEm: Date.now() });
    else
      setEstado((e) => ({
        fase: 'erro',
        falha: r.falha,
        ultimo:
          e.fase === 'pronto'
            ? { dados: e.dados, lidoEm: e.lidoEm }
            : e.fase === 'erro'
              ? e.ultimo
              : null,
      }));
  }, [dia]);

  useEffect(() => {
    setEstado({ fase: 'carregando' });
    void carregar();
  }, [carregar]);

  useEffect(() => {
    // Uma releitura só por volta à tela (antes o intervalo seguia com a aba escondida e somava
    // com a volta); com a aba escondida o intervalo não lê nada (revisão 360).
    let ultima = 0;
    const reler = () => {
      if (document.hidden || Date.now() - ultima < 2_000) return;
      ultima = Date.now();
      void carregar();
    };
    const voltou = () => {
      if (document.visibilityState === 'visible') reler();
    };
    document.addEventListener('visibilitychange', voltou);
    const t = intervaloMs ? window.setInterval(reler, intervaloMs) : undefined;
    return () => {
      document.removeEventListener('visibilitychange', voltou);
      window.clearInterval(t);
    };
  }, [carregar, intervaloMs]);

  return { estado, carregar };
}

/** O dado que a tela mostra: o novo, ou o último bom quando a releitura falhou. */
export function dadosDo(e: EstadoDoPainel) {
  if (e.fase === 'pronto') return { dados: e.dados, lidoEm: e.lidoEm };
  if (e.fase === 'erro') return e.ultimo;
  return null;
}

/** Relógio que anda sozinho (para "há N min" e a hora do Modo TV). */
export function useAgora(passoMs = 30_000) {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setAgora(Date.now()), passoMs);
    return () => window.clearInterval(t);
  }, [passoMs]);
  return agora;
}

const horaDeBrasilia = (d = new Date()) =>
  Number(d.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit' }));

export function saudacao(d = new Date()): string {
  const h = horaDeBrasilia(d);
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

export const primeiroNome = (nome: string | null | undefined) =>
  (nome ?? '').trim().split(/\s+/)[0] ?? '';

export function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p.at(-1)?.[0] ?? '') : '')).toUpperCase();
}

/** 'R$ 42.860,00' → 'R$ 42.860'; centavos diferentes de zero ficam. */
export function dinheiro(v: number): string {
  const r = reais(v);
  return r.endsWith(',00') ? r.slice(0, -3) : r;
}

/** O servidor escreve "1318 de 1600 itens": a tela põe o ponto de milhar. */
export const comMilhar = (t: string) => t.replace(/\d{4,}/g, (n) => inteiro(Number(n)));

export const capitalizar = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

export const primeiraParte = (t: string | null) => (t ?? '').split(' · ')[0] ?? '';

export function minutosAtras(iso: number | string, agora = Date.now()): string {
  const ms = agora - (typeof iso === 'number' ? iso : Date.parse(iso));
  const min = Math.max(0, Math.floor(ms / 60_000));
  if (min < 1) return 'agora';
  if (min < 60) return `há ${String(min)} min`;
  const h = Math.floor(min / 60);
  return `há ${String(h)} h`;
}

export const fonteDo = (f: readonly FrescorDaFonte[], fonte: FrescorDaFonte['fonte']) =>
  f.find((x) => x.fonte === fonte) ?? null;

/**
 * "relatório do HOS recebido às 07:40" — a hora da IMPORTAÇÃO, que o servidor manda no detalhe
 * ("importado em 24/09/2026, 07:40:12"). Outro dia vira "em 23/09 às 07:40".
 */
export function recebimentoDoHos(f: readonly FrescorDaFonte[], hoje: string): string {
  const hos = fonteDo(f, 'relatorio_hos');
  if (!hos || hos.estado === 'indisponivel') return 'nenhuma venda do HOS recebida';
  const m = /(\d{2})\/(\d{2})\/(\d{4}),?\s+(\d{2}:\d{2})/.exec(hos.detalhe);
  if (!m) return `vendas do HOS · ${hos.detalhe}`;
  const [, dd, mm, aaaa, hm] = m;
  return `${aaaa}-${mm}-${dd}` === hoje
    ? `vendas do HOS recebidas às ${hm ?? ''}`
    : `vendas do HOS recebidas em ${dd ?? ''}/${mm ?? ''} às ${hm ?? ''}`;
}

/** Só a hora do recebimento ("07:40"), para o rodapé do Modo TV. */
export function horaDoHos(f: readonly FrescorDaFonte[]): string | null {
  const hos = fonteDo(f, 'relatorio_hos');
  if (!hos || hos.estado === 'indisponivel') return null;
  const m = /(\d{2})\/(\d{2})\/\d{4},?\s+(\d{2}:\d{2})/.exec(hos.detalhe);
  return m ? `${m[3] ?? ''}` : null;
}
